import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

// project import
import AuthLogin from "./auth-forms/AuthLogin";
import AuthWrapper from "./AuthWrapper";
import ForgotPassword from "./auth-forms/ForgotPassword";

// ================================|| SESSION BANNER ||================================ //

const BANNER_DURATION = 20;

const MESSAGES = {
  expired: {
    icon: '⏱',
    heading: 'Session Expired',
    body: 'Your session has expired due to inactivity. Please sign in again to continue.',
  },
  kicked: {
    icon: '⚠',
    heading: 'Signed Out — Logged In Elsewhere',
    body: 'Your account was just signed in on another device. Only one active session is allowed per user, so this session was ended. Please sign in again if this was you.',
  },
};

const SessionBanner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [reason, setReason] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(BANNER_DURATION);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const r = params.get('reason');
    if (r === 'expired' || r === 'kicked') {
      setReason(r);
      // Clean the URL so a manual refresh doesn't re-show the banner
      navigate('/login', { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!reason || dismissed) return;
    if (secondsLeft <= 0) { setDismissed(true); return; }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [reason, secondsLeft, dismissed]);

  if (!reason || dismissed) return null;

  const info = MESSAGES[reason];
  const isKicked = reason === 'kicked';
  const progress = (secondsLeft / BANNER_DURATION) * 100;

  const accentColor  = isKicked ? '#ef4444' : '#f59e0b';
  const accentDark   = isKicked ? '#dc2626' : '#d97706';
  const textColor    = isKicked ? '#7f1d1d' : '#78350f';
  const headingColor = isKicked ? '#991b1b' : '#92400e';
  const bgColor      = isKicked ? 'rgba(254,226,226,0.97)' : 'rgba(254,243,199,0.97)';
  const borderColor  = isKicked ? 'rgba(252,165,165,0.8)' : 'rgba(252,211,77,0.8)';

  return (
    <div style={{
      position: 'fixed',
      top: 76,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      width: 'calc(100% - 32px)',
      maxWidth: 520,
      pointerEvents: 'none',
    }}>
      <div style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 10,
        border: `1px solid ${borderColor}`,
        borderLeft: `4px solid ${accentColor}`,
        background: bgColor,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.15)',
        padding: '14px 42px 18px 16px',
        pointerEvents: 'all',
      }}>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 18, lineHeight: 1.3, flexShrink: 0, marginTop: 1 }}>
            {info.icon}
          </span>
          <div>
            <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '.09em',
              textTransform: 'uppercase',
              color: headingColor,
              marginBottom: 4,
            }}>
              {info.heading}
            </div>
            <div style={{
              fontSize: 13.5,
              fontStyle: 'italic',
              color: textColor,
              lineHeight: 1.6,
            }}>
              {info.body}
            </div>
            <div style={{
              fontSize: 11,
              fontStyle: 'italic',
              color: headingColor,
              marginTop: 6,
              opacity: 0.75,
            }}>
              This notice will disappear in {secondsLeft}s
            </div>
          </div>
        </div>

        {/* close button */}
        <button
          onClick={() => setDismissed(true)}
          style={{
            position: 'absolute', top: 10, right: 10,
            background: 'none', border: 'none', cursor: 'pointer',
            color: headingColor, fontSize: 13, lineHeight: 1,
            padding: 4, opacity: 0.65,
          }}
          aria-label="Dismiss"
        >
          ✕
        </button>

        {/* shrinking countdown bar */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0,
          height: 3,
          width: `${progress}%`,
          background: `linear-gradient(90deg, ${accentColor}, ${accentDark})`,
          transition: 'width 1s linear',
        }} />
      </div>
    </div>
  );
};

// ================================|| LOGIN ||================================ //

const Login = () => {
  const forgotPasswordShow = useSelector((state) => state.forgotPasswordShow);
  const otpShow = useSelector((state) => state.otpShow);
  const loginShow = useSelector((state) => state.loginShow);
  const newpasswordShow = useSelector((state) => state.setpasswordShow);

  const getTitle = () => {
    if (loginShow) return "Login";
    if (forgotPasswordShow && !otpShow && !newpasswordShow) return "Forgot Password?";
    if (otpShow) return "Enter OTP";
    if (newpasswordShow) return "Set New Password";
    return "Login";
  };

  return (
    <>
      {/* Banner is fixed to viewport — renders outside/above the login card */}
      <SessionBanner />

      <AuthWrapper>
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-baseline mb-2">
            <h3 className="text-2xl font-bold text-gray-900 m-0">
              {getTitle()}
            </h3>
          </div>
          <div>
            {loginShow && <AuthLogin />}
            {forgotPasswordShow && <ForgotPassword />}
            {/* {otpShow && <Otp />} */}
          </div>
        </div>
      </AuthWrapper>
    </>
  );
};

export default Login;
